import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, AlertCircle } from "lucide-react";
import { useProviderWaivers } from "@/hooks/useProviderWaivers";
import { useUserWaiverAgreements, useCreateWaiverAgreement } from "@/hooks/useWaiverAgreements";
import { useToast } from "@/hooks/use-toast";

interface WaiverFormProps {
  providerId: string;
  onAllWaiversAgreed: () => void;
}

const WaiverForm = ({ providerId, onAllWaiversAgreed }: WaiverFormProps) => {
  const { toast } = useToast();
  
  const { data: waivers, isLoading: waiversLoading } = useProviderWaivers(providerId);
  const { data: existingAgreements, isLoading: agreementsLoading } = useUserWaiverAgreements(providerId);
  const createAgreement = useCreateWaiverAgreement();

  // Filter out waivers that user has already agreed to
  const pendingWaivers = waivers?.filter(waiver => 
    !existingAgreements?.some(agreement => agreement.waiver_id === waiver.id)
  ) || [];

  const handleSubmitWaivers = async () => {
    try {
      // Create agreements for ALL pending waivers
      for (const waiver of pendingWaivers) {
        await createAgreement.mutateAsync({ providerId, waiverId: waiver.id });
      }

      onAllWaiversAgreed();
    } catch (error) {
      console.error('Error creating waiver agreements:', error);
      toast({
        title: "Error",
        description: "Failed to save waiver agreements. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (waiversLoading || agreementsLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Loading Waivers...</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If no pending waivers, don't show the component
  if (pendingWaivers.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <FileText className="h-5 w-5" />
          <span>Required Waivers</span>
        </CardTitle>
        <div className="flex items-center space-x-2 text-sm text-amber-600">
          <AlertCircle className="h-4 w-4" />
          <span>Please review and agree to the following waivers before booking</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {pendingWaivers.map((waiver) => (
          <div key={waiver.id} className="space-y-3">
            <h4 className="font-semibold text-lg">{waiver.title}</h4>
            <ScrollArea className="h-32 w-full border rounded-md p-3">
              <div className="text-sm whitespace-pre-wrap">
                {waiver.content}
              </div>
            </ScrollArea>
          </div>
        ))}
        
        <Button
          onClick={handleSubmitWaivers}
          disabled={createAgreement.isPending}
          className="w-full"
        >
          {createAgreement.isPending ? "Saving..." : "Accept All Waivers & Continue"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default WaiverForm;
